import { type ChangeEvent, type ReactNode, useEffect, useState } from 'react'
import cn from 'classnames'
import { tr } from 'mmfintech-commons'
import { selectCurrentLanguage, useAppSelector } from 'mmfintech-checkout-commons'
import Select from 'react-select'
import DatePicker from 'react-datepicker'
import PhoneInput from 'react-phone-input-2'
import type { GroupBase, Options, SingleValue, StylesConfig } from 'react-select'
import {
  InputText,
  InputTextArea,
  CustomInputWrapper,
  StyledDatePickerWrapper,
  StyledPhoneInputWrapper,
  CustomInputError
} from './CustomInput.styled'
import ShowIcon from '../../assets/icons/input-eye-black.svg?react'
import HideIcon from '../../assets/icons/input-password-hide.svg?react'
import GlobeIcon from '../../assets/icons/globe.svg?react'
import EmailIcon from '../../assets/icons/input-username.svg?react'
import LockIcon from '../../assets/icons/input-password.svg?react'
import UserIcon from '../../assets/icons/input-select-account-type.svg?react'
import LanguageIcon from '../../assets/icons/input-language.svg?react'
import ReferenceIcon from '../../assets/icons/input-reference.svg?react'

const predefinedIcons = {
  email: <EmailIcon className='fill' />,
  globe: <GlobeIcon />,
  lock: <LockIcon className='fill' />,
  user: <UserIcon className='fill' />,
  language: <LanguageIcon />,
  reference: <ReferenceIcon />
}

type SelectOption = SingleValue<{ label: string; value: string; description?: string }>
type CustomInputType =
  | 'text'
  | 'textarea'
  | 'email'
  | 'password'
  | 'phone'
  | 'int'
  | 'number'
  | 'date'
  | 'select'
  | 'static'
  | 'tel'

type CustomInputProps = {
  id?: string
  icon?: ReactNode
  name?: string
  type: CustomInputType
  error?: string
  label?: string
  value?: string
  options?: Options<SelectOption>
  tooltip?: ReactNode
  onChange?: (name: string, value: string | Date | Date[]) => void
  required?: boolean
  hideLabel?: boolean
  hideBorder?: boolean
  placeholder?: string
  hideRequired?: boolean
  hideErrorLine?: boolean
  hideShowPassword?: boolean

  className?: string
  parentClassName?: string
  [key: string]: any
}

export const CustomInput = (props: CustomInputProps) => {
  const {
    id,
    icon,
    name,
    type,
    error,
    label,
    value,
    options,
    tooltip,
    onChange,
    required = false,
    placeholder,
    hideRequired,
    hideErrorLine,
    hideShowPassword,

    className,
    parentClassName,
    ...rest
  } = props

  const [focused, setFocused] = useState(false)
  const [selectedOption, setSelectedOption] = useState(null)
  const [passwordVisible, setPasswordVisible] = useState(false)

  const selectedLanguage = useAppSelector(selectCurrentLanguage)

  const getRequired = (value: string) => (required && !hideRequired ? '*' : '') + value

  const prepareIcon = () =>
    typeof icon === 'string' && predefinedIcons.hasOwnProperty(icon) ? predefinedIcons[icon] : icon

  const getPlaceholder = () => {
    if (!focused) {
      if (placeholder?.length) return getRequired(placeholder)
      if (label?.length) return getRequired(label)
    }
    return null
  }

  const getShowPasswordIcon = () => {
    if (passwordVisible) {
      return (
        <HideIcon onClick={() => setPasswordVisible(!passwordVisible)} title={tr('FRONTEND.BUTTONS.HIDE', 'hide')} />
      )
    }
    return <ShowIcon onClick={() => setPasswordVisible(!passwordVisible)} title={tr('FRONTEND.BUTTONS.SHOW', 'Show')} />
  }

  const handleBlur = () => setFocused(false)
  const handleEnter = () => setFocused(true)

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onChange && onChange(e.target.name, e.target.value)
  }

  const handleSelectChange = (selectedOption: any) => {
    setSelectedOption(selectedOption)
    onChange && onChange(name, selectedOption.value)
  }

  const isErrorVisible = () => {
    return !hideErrorLine || error?.length > 0
  }

  const isLabelVisible = () => typeof label === 'string' && label.length > 0

  useEffect(() => {
    const list = document.getElementsByClassName('flag-dropdown')
    for (const item of list) {
      item.removeAttribute('tabindex')
    }
  }, [])

  useEffect(() => {
    const option = options?.find(item => item.value === value || item.value === value?.toString())
    setSelectedOption(option)
    // eslint-disable-next-line
  }, [value, options, selectedOption])

  const rules = {}
  if (required !== undefined && required) {
    rules['required'] = true
  }

  const defaultStyles: StylesConfig<any, false, GroupBase<any>> = {
    container: (provided: any) => ({
      ...provided,
      color: '#000000',
      fontFamily: 'inherit',
      fontSize: '1.8rem',
      fontWeight: '400',
      lineHeight: '140%',
      width: '100%'
    }),
    control: (provided: any) => ({
      ...provided,
      border: 'none',
      boxShadow: 'none',
      cursor: 'pointer',
      height: '5rem'
    }),
    valueContainer: (provided: any) => ({
      ...provided,
      padding: 0
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: '#afafaf',
      fontFamily: "'Helvetica Now Display', sans-serif",
      fontSize: '1.4rem',
      fontWeight: '400',
      lineHeight: '140%'
    }),
    dropdownIndicator: (provided: any) => ({
      ...provided,
      paddingRight: 0
    }),
    menu: (provided: any) => ({
      ...provided,
      background: '#ffffff',
      border: 'none',
      borderRadius: '0.5rem',
      padding: '5px 1rem',
      ':hover': {
        border: 'none !important'
      }
    }),
    option: (provided: any) => ({
      ...provided,
      cursor: 'pointer',
      color: value === 'new' ? '#ffffff' : '#000000',
      fontSize: '1.4rem',
      fontWeight: '400',
      lineHeight: '140%',
      textAlign: value === 'new' ? 'center' : 'left',
      borderRadius: value === 'new' ? '2rem' : '0',
      background: value === 'new' ? '#1a1a1a' : '#ffffff',
      width: 'calc(100% - 1rem)',
      marginTop: '3px',
      ':hover': {
        color: '#ffffff',
        background: '#000000',
        borderRadius: '1rem'
      }
    })
  }

  const prepareLabel = () => {
    if (label?.length) {
      return (
        <label htmlFor={id || name}>
          {isLabelVisible() ? (
            <span>
              {required && !hideRequired ? <span className='asterisk'>*</span> : null}
              <span>{label}</span>
            </span>
          ) : (
            ' '
          )}
        </label>
      )
    }
    return null
  }

  const prepareInput = () => {
    if (type === 'static') {
      return (
        <div className={cn('static-input-wrapper')} id={id || name} {...rest}>
          {value || ''}
        </div>
      )
    }

    if (type === 'select') {
      return (
        <Select
          id={id}
          name={name}
          // error={error}
          // label={label}
          placeholder={getPlaceholder()}
          className={cn('select-input', className, { error: error && error.length > 0 })}
          options={options || []}
          value={selectedOption || ''}
          onChange={handleSelectChange}
          styles={defaultStyles}
          required={required}
          components={{
            IndicatorSeparator: () => null
          }}
        />
      )
    }

    if (type === 'date') {
      return (
        <StyledDatePickerWrapper>
          <DatePicker
            locale={selectedLanguage}
            dateFormat={['dd/MM/yyyy', 'dd/MM/yy', 'dd.MM.yyyy', 'dd.MM.yy', 'yyyy-MM-dd']}
            className={cn('date-input', className)}
            selected={value ? new Date(value) : null}
            calendarStartDay={1}
            onChange={date => onChange?.(name, date)}
            placeholderText={label}
            selectsMultiple
            {...rest}
          />
        </StyledDatePickerWrapper>
      )
    }

    if (type === 'phone') {
      return (
        <StyledPhoneInputWrapper>
          <PhoneInput
            enableSearch
            disableSearchIcon
            placeholder={getPlaceholder()}
            value={value || ''}
            inputClass={cn('phone-input', className)}
            onChange={value => onChange && onChange(name, value)}
            inputStyle={{
              border: 'none',
              paddingLeft: '5rem',
              paddingRight: '16px',
              width: '100%'
            }}
            buttonStyle={{
              background: 'transparent',
              border: 'none',
              width: '50px'
            }}
            inputProps={{ id, name, required, ...rest }}
          />
        </StyledPhoneInputWrapper>
      )
    }

    if (type === 'textarea') {
      return (
        <InputTextArea
          {...rest}
          id={id || name}
          name={name}
          className={cn('custom-textarea', className)}
          onChange={handleChange}
          placeholder={getPlaceholder()}
          value={value || ''}
          {...rules}
        />
      )
    }

    return (
      <InputText
        type={(type === 'password' && passwordVisible) || type === 'int' || type === 'number' ? 'text' : type}
        id={id || name}
        name={name}
        value={value || ''}
        placeholder={getPlaceholder()}
        className={cn('custom-input', className, { required: required && !hideRequired })}
        onChange={handleChange}
        onFocus={handleEnter}
        onBlur={handleBlur}
        {...rules}
        {...rest}
      />
    )
  }

  return (
    <CustomInputWrapper
      className={cn('input-wrapper', parentClassName, {
        error: error && error.length > 0
      })}
      data-test={`${type}-input-${name}`}
    >
      <div className={cn('input-inner-wrapper', { error: error && error.length > 0 })}>
        {icon && <span className='left-icons'>{prepareIcon()}</span>}
        <div className='input-container'>
          {(focused || type === 'static') && <label>{prepareLabel()}</label>}
          {prepareInput()}
        </div>
        {(tooltip || (type === 'password' && !hideShowPassword)) && (
          <span className='right-icons'>
            {type === 'password' && !hideShowPassword && getShowPasswordIcon()}
            {type === 'password' && !hideShowPassword && tooltip && <span className='spacer' />}
            {tooltip && <span className='info' data-for='info-tooltip' data-tip={tooltip} />}
          </span>
        )}
      </div>

      {isErrorVisible() ? (
        <CustomInputError className={cn('error-message', { 'left-icon': icon, 'phone-input-error': type === 'phone' })}>
          {error}
        </CustomInputError>
      ) : null}
    </CustomInputWrapper>
  )
}
